import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, ArrowUp, Building, ArrowLeft, Users, Truck, ArrowRight, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import mockData from '../mock';

const FloorSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedDate, hourlyRate, propertyType, propertySize } = location.state || {};
  
  const [selectedFloor, setSelectedFloor] = useState('');

  const floorOptions = [
    { 
      id: 'first', 
      label: 'First Floor / Ground Level', 
      icon: Home,
      description: 'Easy access, no stairs required'
    },
    { 
      id: 'second', 
      label: 'Second Floor', 
      icon: ArrowUp,
      description: 'One flight of stairs'
    },
    { 
      id: 'third', 
      label: 'Third Floor or Higher', 
      icon: ArrowUp,
      description: 'Multiple flights of stairs'
    },
    { 
      id: 'elevator', 
      label: 'Has Elevator Access', 
      icon: Building,
      description: 'Elevator available for moving'
    }
  ];

  // Get crew recommendation based on selections
  const getCrewRecommendation = () => {
    if (!selectedFloor) return null;
    return mockData.getCrewRecommendation(propertyType, propertySize, selectedFloor, false);
  };

  const crewRecommendation = getCrewRecommendation();

  const handleContinue = () => {
    if (selectedFloor && crewRecommendation && !crewRecommendation.callCompany) {
      navigate('/address-input', {
        state: {
          selectedDate,
          hourlyRate,
          propertyType,
          propertySize,
          floorType: selectedFloor,
          crewRecommendation
        }
      });
    }
  };

  const handleGoBack = () => {
    navigate('/size-selection', { 
      state: { selectedDate, hourlyRate },
      replace: true
    });
  };

  const handleFloorSelection = (floorId) => {
    setSelectedFloor(floorId);
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#F8F7F4'}}>
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3 cursor-pointer group">
              <img 
                src="https://customer-assets.emergentagent.com/job_swiftmover/artifacts/7o75lsp7_Emisa%20Movers%20Logo.jpeg" 
                alt="Emisa Movers Logo"
                className="h-28 w-28 transition-all duration-300 transform group-hover:scale-110 group-hover:shadow-2xl group-active:scale-105 rounded-lg"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Emisa Movers</h1>
                <p className="text-xs text-amber-700 font-medium">GETS IT DONE ON TIME</p>
              </div>
            </a>
            <div className="flex items-center gap-3">
              <a 
                href="tel:7134879097"
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 hover:shadow-2xl hover:scale-110 text-white px-4 py-2 rounded-lg font-medium shadow-md transition-all duration-300 transform"
              >
                <Phone className="h-4 w-4" />
                (713) 487-9097
              </a>
              <a href="/" onClick={(e) => { e.preventDefault(); window.location.href = '/#book-now'; }} className="bg-teal-600 hover:bg-teal-700 hover:shadow-2xl hover:scale-110 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 transform">Book It NOW</a>
              <Button
                variant="outline"
                onClick={handleGoBack}
                className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 hover:shadow-xl hover:-translate-y-1 text-white border-gray-600 transition-all duration-300 transform"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Size Selection
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Booking Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4 shadow-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <span className="text-xs text-gray-600">Date:</span>
              <div className="font-medium text-teal-600 text-sm">{selectedDate}</div>
            </div>
            <div>
              <span className="text-xs text-gray-600">Rate:</span>
              <div className="font-medium text-red-600 text-sm">${hourlyRate}/hour</div>
            </div>
            <div>
              <span className="text-xs text-gray-600">Property:</span>
              <div className="font-medium text-teal-600 text-sm capitalize">{propertyType}</div>
            </div>
            <div>
              <span className="text-xs text-gray-600">Size:</span>
              <div className="font-medium text-teal-600 text-sm">{propertySize} sqft</div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Floor & Access Information
          </h1>
          <p className="text-sm sm:text-base text-gray-700">
            Help us determine the right crew size for your move
          </p>
        </div>

        {/* Floor Selection */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-teal-600 mb-4">Select Floor Level</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {floorOptions.map((option) => {
              const Icon = option.icon;
              return (
                <Card
                  key={option.id}
                  className={`cursor-pointer transition-all duration-300 transform hover:shadow-lg ${
                    selectedFloor === option.id 
                      ? 'border-teal-600 bg-teal-50 shadow-md' 
                      : 'border-gray-200 hover:border-teal-600 hover:shadow-lg hover:-translate-y-1 hover:scale-105'
                  }`}
                  onClick={() => handleFloorSelection(option.id)}
                >
                  <CardHeader className="p-4">
                    <div className="flex items-center gap-3">
                      <Icon className={`h-6 w-6 ${
                        selectedFloor === option.id ? 'text-teal-600' : 'text-gray-400'
                      }`} />
                      <div>
                        <CardTitle className="text-base">{option.label}</CardTitle>
                        <p className="text-xs text-gray-600 mt-0.5">{option.description}</p>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Crew Recommendation */}
        {crewRecommendation && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-teal-600 mb-4">Recommended Service</h2>
            <Card className="border-gray-200 bg-white shadow-sm">
              <CardContent className="p-4">
                {crewRecommendation.callCompany ? (
                  <div className="text-center">
                    <div className="text-base font-semibold text-teal-600 mb-2">
                      Custom Service Required
                    </div>
                    <p className="text-sm text-gray-700 mb-3">
                      For this size property, we recommend speaking with our team to create 
                      a customized moving plan that meets your specific needs.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-3">
                      <a 
                        href="tel:7134879097"
                        className="flex items-center justify-center bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium transition-all duration-300"
                      >
                        Call (713) 487-9097
                      </a>
                      <a 
                        href="mailto:info@emisamovers.com"
                        className="flex items-center justify-center border-2 border-teal-600 text-teal-600 hover:bg-teal-50 px-4 py-2 rounded-md font-medium transition-all duration-300"
                      >
                        Email info@emisamovers.com
                      </a>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-3">
                        <Users className="h-8 w-8 text-teal-600" />
                        <div>
                          <div className="text-xl font-bold text-teal-600">
                            {crewRecommendation.men} Movers
                          </div>
                          <p className="text-xs text-gray-600">Professional team</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Truck className="h-8 w-8 text-teal-600" />
                        <div>
                          <div className="text-xl font-bold text-teal-600">
                            {crewRecommendation.trucks} Truck{crewRecommendation.trucks > 1 ? 's' : ''}
                          </div>
                          <p className="text-xs text-gray-600">
                            {crewRecommendation.trucks > 1 ? 'Multiple trucks' : 'Standard truck'}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-center mt-4">
                      <Button
                        onClick={handleContinue}
                        size="lg"
                        className="bg-red-600 hover:bg-red-700 hover:shadow-xl hover:-translate-y-1 hover:scale-105 text-white px-6 py-2 transition-all duration-300 transform"
                      >
                        Continue to Location
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default FloorSelection;